// @flow

import * as React from 'react';
import { findDOMNode } from 'react-dom';
import { connect } from 'react-redux';
import BlockConfirmation from 'app/common/react/components/profile/UserProfileHeader/ModalContents/BlockConfirmation';
import BlockWarning from 'app/common/react/components/profile/UserProfileHeader/ModalContents/BlockWarning';
import BusinessProfileTabBar from 'app/common/react/components/profile/ProfileHeader/BusinessProfileTabBar';
import ContentHeader from 'app/common/react/ui/ContentHeader';
import debounce from 'lodash/debounce';
import i18n from 'app/common/lib/i18n';
// eslint-disable-next-line pinterest/no-deprecated-imports
import LegacyModal from 'app/common/react/ui/deprecated/LegacyModal';
import loadable from '@loadable/component';
import PincodeGenerator from 'app/common/react/components/pincode/PincodeGenerator';
import PincodeModal from 'app/common/react/components/pincode/PincodeModal';
import ProfileCreatorHeader from 'app/common/react/components/profile/ProfileHeader/ProfileCreatorHeader';
import ProfileStatsBar from 'app/common/react/components/profile/ProfileHeader/ProfileStatsBar';
import ProfileUnauthHeader from 'app/common/react/components/growth/unauth/UnauthUserProfilePage/UnauthProfileHeader';
import ReportBug from 'app/common/react/components/profile/UserProfileHeader/ModalContents/ReportBug';
import SendObjectLoader from 'app/common/react/components/sending/SendObjectLoader';
import ShowcaseContainer from 'app/common/react/components/profile/showcase/ShowcaseContainer/ShowcaseContainer';
import type { ProfileUser } from 'app/packages/pinterest-flow-types/user';
import type { State as ReduxState } from 'app/common/redux/reducers/types';
import type { Viewer } from 'app/packages/pinterest-flow-types/viewer';
import UserHeader from 'app/common/react/components/profile/UserHeader/UserHeader';
import withAuthenticatedAction from 'app/common/react/components/limitedLogin/withAuthenticatedAction';
import withViewer from 'app/common/lib/react/withViewer';
import { compose } from 'redux';
import { Experiment } from 'app/common/react/components/experiments';
import { SendObjectTypes } from 'app/common/react/components/sending/SendObjectTypes';
import ProfileTabBar, {
  ProfileTabs,
  type ProfileTabType,
} from 'app/common/react/components/profile/ProfileHeader/ProfileTabBar';
import {
  withRequestContext,
  type RequestContextType,
} from 'app/common/react/components/RequestContext';
import { block, unblock } from 'app/common/redux/actions/user/block';
import { Box, Text } from 'gestalt';

const PartnerProfileCover = loadable(() =>
  import(/* webpackChunkName: "PartnerProfileCover" */ 'app/partner/profileCover/ProfileCover'),
);

type State = {
  currentModal:
    | 'blockConfirmation'
    | 'blockUser'
    | 'sendUser'
    | 'reportBug'
    | 'createPincode'
    | null,
  currentUserDetails: ProfileUser,
  scrollStep: 0 | 1 | 2 | 3,
  showFixedHeaderName: ?boolean,
};

type OwnProps = {
  authAction: *,
  blockUser: (id: string) => void,
  children?: React.Node,
  creatorProfile: boolean,
  isUserBlocked: boolean,
  onClickTab?: Function,
  requestContext: RequestContextType,
  showPulsar?: boolean,
  showSignupModal?: () => void,
  tab: ProfileTabType,
  top: number,
  unblockUser: (id: string) => Promise<Object>,
  user: ProfileUser,
  viewer: Viewer,
};

type ReduxProps = {
  isUserBlocked?: boolean,
  user: Object,
};

type Props = OwnProps & ReduxProps;

export class ProfileHeaderContainer extends React.Component<Props, State> {
  // TODO(xixia): in the current user profile menu, there's a "Pinfluencer" resource fetch.
  // Is it also needed in this one? will sync with PM

  constructor(props: Props) {
    super(props);
    this.state = {
      currentModal: null,
      currentUserDetails: props.user,
      scrollStep: 0,
      showFixedHeaderName: null,
    };
  }

  componentDidMount() {
    const scrollContainer = findDOMNode(this._scrollContainer);
    if (scrollContainer instanceof HTMLElement) {
      document.addEventListener('scroll', this.handleScroll);
    }
  }

  componentWillUnmount() {
    document.removeEventListener('scroll', this.handleScroll);
  }

  setScrollContainer = (ref: ?HTMLDivElement) => (this._scrollContainer = ref);

  handleClickCreatePincode = this.props.authAction(() =>
    this.setState({ currentModal: 'createPincode' }),
  );

  handleClickSendProfile = this.props.authAction(() => this.setState({ currentModal: 'sendUser' }));

  handleHideModal = () => this.setState({ currentModal: null });

  handleHideConfirmationModal = () => {
    this.refreshPage();
  };

  refreshPage = () => {
    window.location.reload(true);
  };

  creatorContainer: ?HTMLElement;

  handleScroll = debounce(() => {
    const scrollContainer = findDOMNode(this._scrollContainer);
    if (scrollContainer instanceof HTMLElement) {
      const { showFixedHeaderName } = this.state;
      if (scrollContainer.getBoundingClientRect().top < this.props.top / 2) {
        if (!showFixedHeaderName) {
          this.setState({ showFixedHeaderName: true });
        }
      } else if (showFixedHeaderName) {
        this.setState({ showFixedHeaderName: false });
      }
    }
  }, 10 /* 100 FPS */);

  handleToggleBlock = this.props.authAction(() => {
    const { isUserBlocked } = this.props;
    if (isUserBlocked) {
      this.unblockUser();
    } else {
      this.setState({ currentModal: 'blockUser' });
    }
  });

  unblockUser = () => {
    const {
      unblockUser,
      user: { id },
    } = this.props;
    this._updateUserDetails({ blocked_by_me: false });
    unblockUser(id).then(() => this.refreshPage());
  };

  handleClickReportBug = () => this.setState({ currentModal: 'reportBug' });

  handleBlockUser = () => {
    const {
      blockUser,
      user: { id },
    } = this.props;
    this._updateUserDetails({ blocked_by_me: true });
    blockUser(id);
    // We are going to optimistically block user - TODO(xixia) add in handling for if the blocking fails
    this.setState({ currentModal: 'blockConfirmation' });
  };

  handleToggleFollow = () => {
    const {
      currentUserDetails: { explicitly_followed_by_me: userFollowedByViewer },
    } = this.state;
    const willBeFollowing = !userFollowedByViewer; // the desired action being taken

    // optimistically toggle the following board state. currently no "loading" state for G Button.
    this._updateUserDetails({ explicitly_followed_by_me: willBeFollowing });
  };

  _headerTopStart: number;
  _scrollContainer: ?HTMLElement;

  _updateUserDetails = (updatedFields: { [key: string]: boolean }) => {
    const { currentUserDetails } = this.state;
    const updatedUser = { ...currentUserDetails, ...updatedFields };
    this.setState({
      currentUserDetails: updatedUser,
    });
  };

  // Disable unauth creator profile for now until bug CON-1384 is fixed
  _shouldRenderCreatorProfileHeader = () =>
    this.props.user.show_creator_profile && this.props.viewer.isAuth;

  renderModal() {
    const { currentModal } = this.state;
    const { user } = this.props;

    switch (currentModal) {
      case 'blockConfirmation':
        return (
          <LegacyModal onHide={this.handleHideConfirmationModal} type="confirm">
            <BlockConfirmation
              firstName={user.first_name}
              onClearModal={this.handleHideConfirmationModal}
            />
          </LegacyModal>
        );
      case 'blockUser':
        return (
          <LegacyModal className="UserBlock" onHide={this.handleHideModal} type="confirm">
            <Box width={380}>
              <BlockWarning
                firstName={user.first_name}
                onBlockUser={this.handleBlockUser}
                onClearModal={this.handleHideModal}
              />
            </Box>
          </LegacyModal>
        );
      case 'reportBug':
        return (
          <LegacyModal onHide={this.handleHideModal}>
            <ReportBug onClearModal={this.handleHideModal} username={user.username} />
          </LegacyModal>
        );
      case 'sendUser':
        const sendUser = {
          description: user.about,
          id: user.id,
          imageUrl: user.image_large_url,
          name: user.full_name,
          // 'pinner' instead of SendObjectTypes.USER because ShareInvite uses that
          // instead of 'user'
          type: SendObjectTypes.PINNER,
          url: `/${user.username}/`,
        };
        const modalStyle = {
          overlay: {},
          content: {
            width: 370,
          },
        };
        return (
          <LegacyModal
            style={modalStyle}
            className="bg-white rounded sendProfileModal"
            onHide={this.handleHideModal}
          >
            <SendObjectLoader
              classNames="SendPinner"
              isFlyout={false}
              onCreatePincodeClick={this.handleClickCreatePincode}
              onHide={this.handleHideModal}
              sendType={SendObjectTypes.USER}
              sharedObject={sendUser}
            />
          </LegacyModal>
        );
      case 'createPincode':
        const AVATAR_IMG_SIZE = 280;
        return (
          <PincodeModal onHide={this.handleHideModal} objectId={user.id}>
            <PincodeGenerator
              id={user.id}
              images={[
                {
                  height: AVATAR_IMG_SIZE,
                  url: user.image_xlarge_url,
                  width: AVATAR_IMG_SIZE,
                },
              ]}
            />
          </PincodeModal>
        );
      default:
        return null;
    }
  }

  renderShowcase() {
    const { user, viewer } = this.props;
    const { has_showcase: hasShowcase, is_partner: isPartner, username } = user;

    const viewingOwnProfile = viewer.isAuth && user.id === viewer.id;
    const showShowcase = isPartner && hasShowcase;

    return (
      showShowcase && (
        <Box>
          <Box display="flex" justifyContent="center">
            <Box marginTop={3} maxWidth={800} paddingX={3} width="100%">
              <Text bold size="xl">
                {i18n._('Featured boards', 'title for showcase')}
              </Text>
            </Box>
          </Box>
          <ShowcaseContainer username={username} viewingOwnProfile={viewingOwnProfile} />
        </Box>
      )
    );
  }

  renderFixedCreatorHeader = () => {
    const transition = 525;
    const transitionStep = 10;
    const { scrollY } = window;
    const upperBoundary = transition + transitionStep;
    const lowerBoundary = transition - transitionStep;

    if (scrollY > upperBoundary) {
      this.setState({ scrollStep: 3 });
    } else if (scrollY > transition) {
      this.setState({ scrollStep: 2 });
    } else if (scrollY > lowerBoundary) {
      this.setState({ scrollStep: 1 });
    } else {
      this.setState({ scrollStep: 0 });
    }
  };

  renderCreatorProfileHeader = () => {
    const { children, onClickTab, tab, viewer, user } = this.props;
    const { currentModal, currentUserDetails, scrollStep, showFixedHeaderName } = this.state;

    const creatorClass =
      scrollStep > 0 ? 'CreatorHeaderSticky opaqueScroll' : 'CreatorHeaderSticky';

    return (
      <React.Fragment>
        <Experiment name="web_business_profile_cover_asset">
          {({ anyEnabled: profileCoverAssetEnabled }) => (
            <PartnerProfileCover user={user} profileCoverAssetEnabled={profileCoverAssetEnabled} />
          )}
        </Experiment>
        <Box>
          <Box marginLeft={3} marginRight={3}>
            <ProfileCreatorHeader
              onClickReportBug={this.handleClickReportBug}
              onClickSendProfile={this.handleClickSendProfile}
              onCreatePincodeClick={this.handleClickCreatePincode}
              onToggleBlock={this.handleToggleBlock}
              onToggleFollow={this.handleToggleFollow}
              showFixedHeaderName={showFixedHeaderName}
              user={currentUserDetails}
              onScroll={this.renderFixedCreatorHeader}
              scrollStep={scrollStep}
            />
          </Box>
          <Box justifyContent="center" display="flex">
            <Box>
              <ContentHeader className={creatorClass}>
                {viewer.isAuth ? (
                  <Experiment name="web_creators_story_pin_display">
                    {({ anyEnabled: storyPinDisplayEnabled }) => (
                      <BusinessProfileTabBar
                        onClickTab={onClickTab}
                        tab={tab}
                        user={user}
                        storyPinDisplayEnabled={storyPinDisplayEnabled}
                      />
                    )}
                  </Experiment>
                ) : (
                  <ProfileTabBar onClickTab={onClickTab} tab={tab} user={user} />
                )}
                <ProfileStatsBar tab={tab} user={user} alignCenter />
              </ContentHeader>
              {[ProfileTabs.OVERVIEW].includes(tab) && this.renderShowcase()}
              {children}
            </Box>
          </Box>
        </Box>
        {currentModal && this.renderModal()}
      </React.Fragment>
    );
  };

  renderProfile() {
    const { onClickTab, showPulsar, tab, top, user, viewer } = this.props;
    const { currentModal, currentUserDetails, showFixedHeaderName } = this.state;
    // TODO(xixia): Consider separating out the fixed header from the main header.
    return (
      <div className="BrioProfileHeaderWrapper" ref={this.setScrollContainer}>
        <UserHeader
          isProfile
          onCreatePincodeClick={this.handleClickCreatePincode}
          onToggleBlock={this.handleToggleBlock}
          onToggleFollow={this.handleToggleFollow}
          showFixedHeaderName={showFixedHeaderName}
          showPulsar={showPulsar}
          top={top}
          user={currentUserDetails}
        />
        {tab === ProfileTabs.BOARDS && this.renderShowcase()}
        {viewer.isAuth && <ProfileTabBar onClickTab={onClickTab} tab={tab} user={user} />}
        {tab !== ProfileTabs.BOARDS && <ProfileStatsBar tab={tab} user={user} />}
        {currentModal && this.renderModal()}
      </div>
    );
  }

  renderUnauthProfile() {
    const { showSignupModal } = this.props;
    const { currentModal, currentUserDetails, scrollStep, showFixedHeaderName } = this.state;
    return (
      <div className="BrioProfileHeaderWrapper" ref={this.setScrollContainer}>
        <ProfileUnauthHeader
          scrollStep={scrollStep}
          showFixedHeaderName={showFixedHeaderName}
          showSignupModal={showSignupModal}
          user={currentUserDetails}
        />
        {currentModal && this.renderModal()}
      </div>
    );
  }

  render() {
    const { viewer } = this.props;
    if (!viewer.isAuth) {
      return this.renderUnauthProfile();
    } else if (this._shouldRenderCreatorProfileHeader()) {
      return this.renderCreatorProfileHeader();
    } else {
      return this.renderProfile();
    }
  }
}

// $FlowFixMe: Please add "import type { Dispatch } from 'redux';"
const mapDispatchToProps = (dispatch: Dispatch<any>) => ({
  blockUser: (id) => dispatch(block(id)),
  unblockUser: (id) => dispatch(unblock(id)),
});

export default compose(
  withViewer,
  withAuthenticatedAction,
  withRequestContext,
  connect(
    (state: ReduxState, { user, user: { id } }: OwnProps): $Exact<ReduxProps> => {
      const currentUser = state.users && state.users[id] ? state.users[id] : user;
      return {
        isUserBlocked: currentUser && currentUser.blocked_by_me,
        user: currentUser,
      };
    },
    mapDispatchToProps,
  ),
)(ProfileHeaderContainer);
